import { useEffect, useMemo, useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { isMobile } from 'react-device-detect';
import { SvgIcon } from '@quicktools/shared-components';

import { useStore } from '../../store';
import { ActionTypesEnum, ExportStatusEnum } from '../../store/types';
import { ColorCombinationOptions } from '../../constants/const';
import rgbToRyb from '../../utils/common/rgbToRyb';
import { changeColorBrightness } from '../../utils/common/changeColorBrightness';
import { BASE_URL } from '../../configs';

import useStyles from './styles';

let TIMEOUT_ID: ReturnType<typeof setTimeout> | null;

function PDFView() {
  const {
    state: { exportStatus, mainColorCombination, colorCombination },
    dispatch,
  } = useStore();

  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const selectedCombination = ColorCombinationOptions[colorCombination].combinatedColors;
  const boxes = new Array(selectedCombination.length).fill(null);
  const styles = useStyles({
    isVisible,
    boxCount: boxes.length,
    scale: isMobile ? 5 : 5,
  });

  const pdfColorsList = useMemo(() => {
    return boxes.map((_, index) => {
      const colorByIndex = mainColorCombination[selectedCombination[index]];
      const color = changeColorBrightness(colorByIndex.color, colorByIndex.brightness);
      const hexCode = color.hex().toString();
      const rgbCode = color
        .rgb()
        .array()
        .map((item: number) => Math.floor(item));
      const rybCode = rgbToRyb(rgbCode).map((item: number) => Math.floor(item));

      return {
        hexCode,
        rgbCode: rgbCode.toString(),
        rybCode: rybCode.toString(),
      };
    });
  }, [boxes, mainColorCombination, selectedCombination]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (exportStatus === ExportStatusEnum.STARTED) {
      setIsVisible(true);
    }
  }, [exportStatus]);

  useEffect(() => {
    TIMEOUT_ID = setTimeout(() => {
      if (isVisible) {
        if (ref.current === null) {
          return;
        }

        toPng(ref.current, { cacheBust: true })
          .then((dataUrl) => {
            const doc = new jsPDF();
            const colorCodeItems = new Array(pdfColorsList.length).fill(0);

            switch (pdfColorsList.length) {
              case 2:
                colorCodeItems[0] = 68;
                colorCodeItems[1] = 148;
                break;
              case 3:
                colorCodeItems[0] = 55;
                colorCodeItems[1] = 110;
                colorCodeItems[2] = 162;
                break;
              case 4:
                colorCodeItems[0] = 49;
                colorCodeItems[1] = 89;
                colorCodeItems[2] = 129;
                colorCodeItems[3] = 169;
                break;
            }

            doc.addImage(dataUrl, 'png', 0, 0, 210, 300);
            doc.setFontSize(10);
            const colorsForUrl = pdfColorsList.map(({ hexCode }) => hexCode.slice(1)).join('+');
            doc.text(
              `${BASE_URL}/?colors=${colorsForUrl}&combination=${colorCombination}`,
              105,
              260,
              { align: 'center' },
            );
            doc.setFontSize(10);
            doc.setTextColor(8, 8, 8);
            pdfColorsList.forEach((item, index) => {
              const { hexCode, rgbCode, rybCode } = item;

              doc.text(hexCode, colorCodeItems[index], 134, {
                align: 'center',
              });
              doc.text(rgbCode, colorCodeItems[index], 143, {
                align: 'center',
              });
              doc.text(rybCode, colorCodeItems[index], 152.5, {
                align: 'center',
              });
            });

            doc.save('color-wheel.pdf');

            dispatch({
              type: ActionTypesEnum.SET_EXPORT_STATUS,
              payload: ExportStatusEnum.DONE,
            });
            setIsVisible(false);
          })
          .catch((err) => {
            console.log(err);
          });
      }
    }, 100);

    return () => {
      TIMEOUT_ID && clearTimeout(TIMEOUT_ID);
    };
  }, [isVisible, dispatch, pdfColorsList]);

  const renderColors = useMemo(() => {
    return pdfColorsList.map((item, index) => {
      const { hexCode } = item;

      return (
        <>
          <div className={styles.PDFContainerColorsItem} key={index}>
            <div
              className={styles.PDFContainerColorsItemColor}
              style={{ backgroundColor: hexCode }}
            ></div>
            <div className={styles.PDFContainerColorsItemSpace}></div>
            <div className={styles.PDFContainerColorsItemInfo}>
              <span className={styles.PDFContainerColorsItemInfoType}>Hex</span>{' '}
              <p className={styles.PDFContainerColorsItemInfoCode}>{/* {hexCode} */}</p>
            </div>
            <div className={styles.PDFContainerColorsItemInfo}>
              <span className={styles.PDFContainerColorsItemInfoType}>RGB</span>{' '}
              <p className={styles.PDFContainerColorsItemInfoCode}>{/* {rgbCode} */}</p>
            </div>
            <div className={styles.PDFContainerColorsItemInfo}>
              <span className={styles.PDFContainerColorsItemInfoType}>RYB</span>{' '}
              <p className={styles.PDFContainerColorsItemInfoCode}>{/* {rybCode} */}</p>
            </div>
          </div>
        </>
      );
    });
  }, [pdfColorsList, styles]);

  return (
    <div className={styles.root} aria-label="header">
      <div ref={ref} className={styles.PDF}>
        <div className={styles.PDFContainer}>
          <p className={styles.PDFContainerTitle}>Color Wheel</p>
          <p className={styles.PDFContainerDescription}>{colorCombination} Mode</p>
          <div className={styles.PDFContainerColors}>{renderColors}</div>

          <p className={styles.PDFContainerURL}>{/* https://tools.picsart.com/color/wheel/ */}</p>
          <div className={styles.PDFContainerFooter}>
            <SvgIcon className={styles.PDFContainerFooterLogo} name="color-wheel-gen" />
            <span className={styles.PDFContainerFooterMain}>Color</span>
            <span className={styles.PDFContainerFooterText}>Wheel</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PDFView;
